/**
 * MyosFormFillModal — real-user fill & submit for MYOS form templates (§11).
 * Renders the company template dynamically (visible/conditional/required
 * fields honored), validates, and POSTs a submission — optionally tied to an
 * appointment so workflow-wired gates unblock.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Loader2, Clock, Camera } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const tokenHeaders = () => {
  const tok = localStorage.getItem('token') || localStorage.getItem('access_token') || '';
  return tok ? { Authorization: `Bearer ${tok}` } : {};
};

const conditionMet = (f, values) => {
  const c = f.condition;
  if (!c) return true;
  const v = values[c.field];
  if (c.operator === 'equals') {
    return String(v || '').trim().toLowerCase() === String(c.value || '').trim().toLowerCase();
  }
  return v !== undefined && v !== null && v !== '' && v !== false;
};

export const MyosFormFillModal = ({ templateId, appointmentId, onClose, onDone }) => {
  const [template, setTemplate] = useState(null);
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [missing, setMissing] = useState([]);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/api/myos/forms/templates/${templateId}`, { headers: tokenHeaders() })
      .then((r) => { if (alive) setTemplate(r.data.template); })
      .catch(() => { toast.error('Could not load the form.'); onClose?.(); });
    return () => { alive = false; };
  }, [templateId, onClose]);

  const setVal = (id, v) => setValues((p) => ({ ...p, [id]: v }));

  const handlePhoto = (id, file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Photo too large (max 5MB).');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setVal(id, reader.result);
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    setSaving(true);
    setMissing([]);
    try {
      await axios.post(
        `${API}/api/myos/forms/templates/${templateId}/submit`,
        { values, appointment_id: appointmentId || null },
        { headers: tokenHeaders() }
      );
      toast.success(`${template?.name || 'Form'} submitted.`);
      onDone?.();
      onClose?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.error === 'missing_required_fields') {
        setMissing(d.missing || []);
        toast.error(`Missing required: ${(d.missing || []).join(', ')}`);
      } else {
        toast.error(typeof d === 'string' ? d : d?.message || 'Could not submit the form.');
      }
    } finally {
      setSaving(false);
    }
  };

  const renderInput = (f) => {
    const base = 'w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400';
    const tid = `myos-fill-field-${f.id}`;
    switch (f.type) {
      case 'textarea':
        return <textarea data-testid={tid} rows={3} className={base} value={values[f.id] || ''} onChange={(e) => setVal(f.id, e.target.value)} />;
      case 'number':
        return <input data-testid={tid} type="number" className={base} value={values[f.id] || ''} onChange={(e) => setVal(f.id, e.target.value)} />;
      case 'date':
        return <input data-testid={tid} type="date" className={base} value={values[f.id] || ''} onChange={(e) => setVal(f.id, e.target.value)} />;
      case 'time':
        return <input data-testid={tid} type="time" className={base} value={values[f.id] || ''} onChange={(e) => setVal(f.id, e.target.value)} />;
      case 'checkbox':
        return (
          <label className="flex items-center gap-2 text-sm">
            <input data-testid={tid} type="checkbox" checked={!!values[f.id]} onChange={(e) => setVal(f.id, e.target.checked)} />
            Yes
          </label>
        );
      case 'select':
        return (
          <select data-testid={tid} className={base} value={values[f.id] || ''} onChange={(e) => setVal(f.id, e.target.value)}>
            <option value="">Select…</option>
            {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        );
      case 'photo':
        return (
          <div>
            <label data-testid={tid} className="flex items-center gap-2 text-sm px-3 py-2 border border-dashed border-slate-300 rounded-lg cursor-pointer hover:bg-slate-50">
              <Camera className="w-4 h-4 text-slate-500" />
              {values[f.id] ? 'Photo attached — tap to replace' : 'Take / upload photo'}
              <input type="file" accept="image/*" capture="environment" className="hidden"
                onChange={(e) => handlePhoto(f.id, e.target.files?.[0])} />
            </label>
            {values[f.id] && <img src={values[f.id]} alt={f.label} className="mt-2 h-20 rounded-lg object-cover" />}
          </div>
        );
      case 'signature':
        return (
          <div>
            <input data-testid={tid} type="text" placeholder="Type your full name to sign"
              className={`${base} italic`} style={{ fontFamily: 'cursive' }}
              value={values[f.id] || ''} onChange={(e) => setVal(f.id, e.target.value)} />
            <p className="text-[11px] text-slate-400 mt-1">Typing your name acts as your signature.</p>
          </div>
        );
      case 'timestamp':
        return (
          <div data-testid={tid} className="flex items-center gap-1.5 text-xs text-slate-500 px-3 py-2 bg-slate-50 rounded-lg">
            <Clock className="w-3.5 h-3.5" /> Captured automatically when you submit
          </div>
        );
      default:
        return <input data-testid={tid} type="text" className={base} value={values[f.id] || ''} onChange={(e) => setVal(f.id, e.target.value)} />;
    }
  };

  return (
    <div className="fixed inset-0 z-[9990] bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="myos-fill-modal">
      <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl max-h-[90vh] flex flex-col">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
          <h3 className="font-semibold text-sm">{template?.name || 'Loading…'}</h3>
          <button data-testid="myos-fill-close" onClick={onClose} className="ml-auto p-1 hover:bg-slate-100 rounded">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>
        <div className="p-4 space-y-3 overflow-y-auto">
          {!template ? (
            <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
          ) : (
            (template.fields || [])
              .filter((f) => f.visible !== false && conditionMet(f, values))
              .map((f) => (
                <div key={f.id}>
                  <label className={`block text-xs font-medium mb-1 ${missing.includes(f.label) ? 'text-rose-600' : 'text-slate-600'}`}>
                    {f.label}{f.required && <span className="text-rose-500 ml-0.5">*</span>}
                  </label>
                  {renderInput(f)}
                </div>
              ))
          )}
        </div>
        <div className="p-4 border-t border-slate-100">
          <button
            data-testid="myos-fill-submit"
            onClick={submit}
            disabled={saving || !template}
            className="w-full py-2.5 text-sm font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Submit form
          </button>
        </div>
      </div>
    </div>
  );
};

export default MyosFormFillModal;
